// Heuristic check: does the email's local-part look like it belongs to a DIFFERENT
// person than the applicant? Loose by design — generic handles like "cutechick123"
// or "yorkshireterrier" stay silent. Only fires when the local-part contains a
// recognised common first name that isn't the applicant's first name.
//
// Returns either:
//   null                              — no concern (silent)
//   { detectedName: 'John' }          — local-part looks like a different person
//
// Used by:
//   - PersonalAndContactStep (real-time amber warning under the email field)
//   - ReviewStep (amber strip on the Personal Details section)
//   - OperatorMode (same warning, staff-facing)

// Shared / role-based prefixes — never flag these. Business apps and reception
// inboxes commonly come through this way and a name mismatch is expected.
const SHARED_PREFIXES = new Set([
  'info', 'sales', 'admin', 'accounts', 'account', 'contact', 'hello',
  'enquiries', 'enquiry', 'office', 'support', 'help', 'team', 'marketing',
  'finance', 'noreply', 'no-reply', 'donotreply', 'reception', 'reservations',
  'bookings', 'invoices', 'billing', 'orders', 'orders1', 'service', 'mail',
  'post', 'admissions', 'hr', 'careers', 'jobs', 'press', 'media', 'general',
  'main', 'inbox', 'feedback', 'customerservice', 'customercare',
]);

// Common UK first names (top occurrences across ONS birth registers + historical
// frequency). Length 3+ to avoid false matches on initials. Lowercased.
// Intentionally over-includes traditional names (still in active applicant pool).
const COMMON_FIRST_NAMES = new Set([
  // Men — modern & traditional
  'aaron', 'abdul', 'adam', 'adrian', 'aiden', 'alan', 'albert', 'alex',
  'alexander', 'alfie', 'alfred', 'ali', 'allen', 'andrew', 'andy', 'angus',
  'anthony', 'antony', 'archie', 'arthur', 'ashley', 'austin', 'barry',
  'ben', 'benjamin', 'bernard', 'billy', 'bobby', 'bradley', 'brandon',
  'brendan', 'brian', 'bruce', 'bryan', 'callum', 'calvin', 'cameron',
  'carl', 'cecil', 'charles', 'charlie', 'chris', 'christian', 'christopher',
  'clarence', 'clark', 'clifford', 'clive', 'cody', 'colin', 'connor',
  'craig', 'curtis', 'cyril', 'dale', 'damian', 'dan', 'daniel', 'danny',
  'darren', 'darryl', 'dave', 'david', 'dean', 'declan', 'dennis', 'derek',
  'derrick', 'desmond', 'dexter', 'dick', 'don', 'donald', 'douglas', 'drew',
  'duncan', 'dwayne', 'dylan', 'eamon', 'ed', 'eddie', 'edgar', 'edmund',
  'edward', 'edwin', 'elliot', 'elliott', 'ellis', 'emmanuel', 'eric',
  'ernest', 'ethan', 'eugene', 'evan', 'felix', 'finlay', 'finley', 'finn',
  'francis', 'frank', 'franklin', 'fraser', 'fred', 'freddie', 'frederick',
  'gabriel', 'gareth', 'gary', 'gavin', 'gene', 'geoffrey', 'george',
  'gerald', 'gerard', 'gilbert', 'giles', 'glen', 'glenn', 'gordon', 'graham',
  'grant', 'gregory', 'guy', 'hamish', 'harold', 'harrison', 'harry',
  'harvey', 'henry', 'herbert', 'horace', 'howard', 'hugh', 'hugo', 'iain',
  'ian', 'isaac', 'ivan', 'jack', 'jackson', 'jacob', 'jake', 'james',
  'jamie', 'jared', 'jason', 'jasper', 'jay', 'jayden', 'jed', 'jeff',
  'jeffrey', 'jeremy', 'jerome', 'jerry', 'jesse', 'jim', 'jimmy', 'joe',
  'joel', 'john', 'johnathan', 'johnny', 'jon', 'jonas', 'jonathan',
  'jordan', 'joseph', 'josh', 'joshua', 'julian', 'justin', 'karl', 'keith',
  'kelvin', 'ken', 'kenneth', 'kenny', 'kevin', 'kieran', 'kirk', 'kyle',
  'lance', 'larry', 'laurence', 'lawrence', 'lee', 'leo', 'leon', 'leonard',
  'leroy', 'leslie', 'lewis', 'liam', 'lincoln', 'lionel', 'lloyd', 'logan',
  'louis', 'lucas', 'luke', 'malcolm', 'marc', 'marcus', 'mark', 'martin',
  'mason', 'matt', 'matthew', 'maurice', 'max', 'melvin', 'micah', 'michael',
  'mick', 'mickey', 'miles', 'milo', 'mitchell', 'mohammad', 'mohammed',
  'morgan', 'muhammad', 'murray', 'nathan', 'nathaniel', 'neil', 'nelson',
  'nick', 'nicholas', 'nigel', 'noah', 'noel', 'norman', 'olaf', 'oliver',
  'omar', 'oscar', 'otis', 'owen', 'pat', 'patrick', 'paul', 'percy',
  'perry', 'pete', 'peter', 'philip', 'phillip', 'preston', 'quentin',
  'quincy', 'quinn', 'ralph', 'randall', 'randy', 'raphael', 'raymond',
  'reece', 'reggie', 'reginald', 'reuben', 'rex', 'rhys', 'richard', 'rick',
  'ricky', 'riley', 'robert', 'robbie', 'robin', 'rodney', 'roger', 'roland',
  'ron', 'ronald', 'ronnie', 'rory', 'ross', 'roy', 'royston', 'rudolph',
  'rufus', 'rupert', 'russell', 'ryan', 'sam', 'samuel', 'scott', 'sean',
  'sebastian', 'seth', 'shane', 'shaun', 'shawn', 'sidney', 'simon', 'sol',
  'solomon', 'spencer', 'stan', 'stanley', 'stefan', 'stephen', 'stevan',
  'steve', 'steven', 'stewart', 'stuart', 'sydney', 'taylor', 'ted', 'teddy',
  'terence', 'terry', 'theo', 'theodore', 'thomas', 'tim', 'timothy', 'toby',
  'todd', 'tom', 'tommy', 'tony', 'trevor', 'tristan', 'troy', 'tyler',
  'tyrone', 'val', 'vance', 'vernon', 'victor', 'vincent', 'walter', 'warren',
  'wayne', 'wesley', 'william', 'willie', 'wyatt', 'xander', 'yousef',
  'zachary', 'zack', 'zane',

  // Women — modern & traditional
  'abigail', 'ada', 'agnes', 'aileen', 'aimee', 'alana', 'alexandra',
  'alexis', 'alice', 'alicia', 'alison', 'allison', 'alma', 'amanda', 'amber',
  'amelia', 'amy', 'andrea', 'angela', 'angelina', 'anita', 'ann', 'anna',
  'anne', 'annette', 'annie', 'antonia', 'april', 'ariana', 'arlene', 'ashley',
  'aubrey', 'audrey', 'aurora', 'autumn', 'ava', 'barbara', 'beatrice',
  'becky', 'belinda', 'bella', 'belle', 'bernadette', 'bernice', 'beryl',
  'beth', 'bethany', 'betsy', 'betty', 'beverly', 'bianca', 'bonnie',
  'brenda', 'briana', 'brianna', 'bridget', 'brittany', 'brooke', 'caitlin',
  'camilla', 'candace', 'cara', 'carla', 'carly', 'carmen', 'carol',
  'caroline', 'carolyn', 'carrie', 'cassandra', 'cassie', 'catherine',
  'cathleen', 'cathy', 'cecilia', 'celeste', 'celia', 'charlene', 'charlotte',
  'chelsea', 'cheryl', 'chloe', 'christina', 'christine', 'cindy', 'claire',
  'clara', 'clare', 'claudia', 'colleen', 'connie', 'constance', 'cora',
  'cornelia', 'courtney', 'crystal', 'cynthia', 'daisy', 'dana', 'danielle',
  'daphne', 'darlene', 'dawn', 'deanna', 'debbie', 'debra', 'deirdre',
  'delia', 'della', 'denise', 'diana', 'diane', 'dolores', 'donna', 'dora',
  'doreen', 'doris', 'dorothy', 'edith', 'edna', 'eileen', 'elaine',
  'eleanor', 'elena', 'eliza', 'elizabeth', 'ella', 'ellen', 'ellie',
  'elsie', 'emilia', 'emily', 'emma', 'enid', 'erica', 'erin', 'estelle',
  'esther', 'ethel', 'eva', 'eve', 'evelyn', 'evie', 'faith', 'fay', 'faye',
  'felicia', 'felicity', 'fern', 'fiona', 'flora', 'florence', 'frances',
  'francesca', 'frankie', 'freda', 'freya', 'gabriella', 'gabrielle', 'gail',
  'gemma', 'georgia', 'georgina', 'geraldine', 'gillian', 'gina', 'gladys',
  'glenda', 'gloria', 'grace', 'gwen', 'gwendoline', 'hannah', 'harriet',
  'hayley', 'hazel', 'heather', 'heidi', 'helen', 'helena', 'henrietta',
  'hilda', 'holly', 'honor', 'hope', 'ida', 'imogen', 'ingrid', 'irene',
  'iris', 'isabel', 'isabella', 'isabelle', 'isla', 'ivy', 'jackie',
  'jacqueline', 'jade', 'jamie', 'jane', 'janet', 'janice', 'janine',
  'jasmine', 'jean', 'jeanette', 'jeanne', 'jenna', 'jennifer', 'jenny',
  'jessica', 'jessie', 'jill', 'jo', 'joan', 'joanna', 'joanne', 'jocelyn',
  'jodie', 'joelle', 'jolene', 'josephine', 'joy', 'joyce', 'judith', 'judy',
  'julia', 'julie', 'juliet', 'julietta', 'june', 'kara', 'karen', 'karla',
  'kate', 'katherine', 'kathleen', 'kathryn', 'kathy', 'katie', 'katrina',
  'kay', 'kayla', 'kelly', 'kelsey', 'kerry', 'kim', 'kimberly', 'kirsten',
  'kirsty', 'kristen', 'kylie', 'lana', 'lara', 'laura', 'lauren', 'laurie',
  'layla', 'leah', 'leanne', 'leona', 'leonie', 'leslie', 'libby', 'lila',
  'lillian', 'lilly', 'lily', 'linda', 'lindsey', 'lisa', 'lois', 'lola',
  'lori', 'lorna', 'lorraine', 'louisa', 'louise', 'lucia', 'lucinda',
  'lucy', 'luisa', 'lydia', 'lyn', 'lynda', 'lynn', 'lynne', 'mabel',
  'madeline', 'madison', 'maeve', 'maggie', 'mandy', 'marcia', 'margaret',
  'margery', 'marguerite', 'maria', 'marian', 'marie', 'marilyn', 'marion',
  'marjorie', 'martha', 'mary', 'maureen', 'mavis', 'maxine', 'maya', 'meg',
  'megan', 'melanie', 'melissa', 'mia', 'michelle', 'mildred', 'millie',
  'miranda', 'miriam', 'misty', 'molly', 'monica', 'morgan', 'muriel',
  'myra', 'myrtle', 'nadia', 'nadine', 'nancy', 'naomi', 'natalia',
  'natalie', 'natasha', 'nellie', 'nicola', 'nicole', 'nina', 'noelle',
  'nora', 'norah', 'norma', 'olive', 'olivia', 'paige', 'pamela', 'patience',
  'patricia', 'patsy', 'patty', 'paula', 'paulette', 'pauline', 'pearl',
  'peggy', 'penelope', 'penny', 'phoebe', 'phyllis', 'polly', 'poppy',
  'priscilla', 'rachael', 'rachel', 'rebecca', 'regina', 'rene', 'renee',
  'rhoda', 'rhonda', 'rita', 'roberta', 'robin', 'rochelle', 'rosa',
  'rosalind', 'rose', 'rosemary', 'rosie', 'rowena', 'ruby', 'ruth',
  'sadie', 'sally', 'samantha', 'sandra', 'sara', 'sarah', 'sasha', 'scarlet',
  'scarlett', 'selena', 'serena', 'shannon', 'sharon', 'sheila', 'shelly',
  'sheryl', 'shirley', 'sienna', 'sofia', 'sonia', 'sophia', 'sophie',
  'stacey', 'stacy', 'stella', 'stephanie', 'sue', 'susan', 'susanna',
  'susannah', 'susie', 'suzanne', 'sybil', 'sylvia', 'tabitha', 'tamara',
  'tammy', 'tania', 'tanya', 'tara', 'teresa', 'tessa', 'thelma', 'theresa',
  'tiffany', 'tina', 'toni', 'tonya', 'tracey', 'tracy', 'tricia', 'trina',
  'trish', 'trisha', 'trudy', 'valerie', 'vanessa', 'vera', 'verity',
  'veronica', 'vicki', 'vicky', 'victoria', 'violet', 'virginia', 'vivian',
  'vivienne', 'wanda', 'wendy', 'whitney', 'wilma', 'winifred', 'yasmin',
  'yolanda', 'yvette', 'yvonne', 'zara', 'zoe',
]);

const isString = (v) => typeof v === 'string';

// Pull the local-part out (before @). Trims whitespace. Returns lowercased or null.
const localPartOf = (email) => {
  if (!isString(email)) return null;
  const at = email.indexOf('@');
  if (at <= 0) return null;
  return email.slice(0, at).trim().toLowerCase();
};

// Compact letter-only blob from local-part (strip digits, dots, underscores, dashes).
const lettersOnly = (s) => s.replace(/[^a-z]/g, '');

// Capitalize first letter
const capitalise = (s) => (s ? s[0].toUpperCase() + s.slice(1) : s);

export const detectEmailNameMismatch = ({ email, firstName, surname } = {}) => {
  if (!isString(email) || !email.includes('@')) return null;
  if (!isString(firstName) || !isString(surname)) return null;
  const first = firstName.trim().toLowerCase();
  const last = surname.trim().toLowerCase();
  if (first.length < 2 || last.length < 2) return null;

  const local = localPartOf(email);
  if (!local) return null;

  // 1. Shared / role-based prefixes — silent.
  // Match the prefix portion before any digit, dot, dash or plus.
  const prefixCore = local.split(/[.\-_+0-9]/, 1)[0];
  if (SHARED_PREFIXES.has(prefixCore) || SHARED_PREFIXES.has(local)) {
    return null;
  }

  // 2. Letter blob (e.g. "johnsmith109" → "johnsmith", "a.parker_99" → "aparker")
  const blob = lettersOnly(local);
  if (blob.length < 3) return null;

  // 3. First name (3+ chars) appears in blob → applicant name present, OK.
  if (first.length >= 3 && blob.includes(first)) return null;

  // 4. Surname (4+ chars) appears in blob → applicant name present, OK.
  //    Min 4 to avoid coincidental short matches like "lee" inside "lloyd".
  if (last.length >= 4 && blob.includes(last)) return null;

  // 5. Common initials patterns: f0+surname, firstName+s0, f0+s0
  //    e.g. Annette Parker → aparker / annettep / ap
  if (last.length >= 3 && blob.includes(first.charAt(0) + last)) return null;
  if (first.length >= 3 && blob.includes(first + last.charAt(0))) return null;

  // 6. Scan blob for any common first name (3+ chars) that ISN'T the applicant's.
  //    First check the front of the blob (most common: johnsmith, sarahjones), then
  //    fall back to any-substring scan. Longest match wins to avoid e.g. matching
  //    "ann" inside "annette" — but the applicant's own name was already cleared
  //    in step 3, so this catches genuine other-name cases.
  let detected = null;
  for (const name of COMMON_FIRST_NAMES) {
    if (name === first) continue;
    if (name.length < 4) continue; // looser: skip 3-letter names to reduce false positives ("ann", "rob", "sam")
    if (blob.includes(name)) {
      if (!detected || name.length > detected.length) detected = name;
    }
  }
  if (detected) return { detectedName: capitalise(detected) };

  return null;
};

export default detectEmailNameMismatch;
